/**
 * De teksten op de site: de intro op de muur, wat een held ervoor terugkrijgt,
 * de tekst bij de bedrijven, de voorwaarden en de privacyverklaring.
 *
 * Met opzet in de code en niet in de database (besluit van 7 oktober 2026):
 * een tekst die in het beheer te wijzigen is loopt op test en productie
 * uit elkaar zodra iemand hem op één van beide aanpast. Hier gaat hij mee in
 * Git, en met een uitrol staat hij overal hetzelfde. Het bestuur stelt de
 * voorwaarden en de privacyverklaring vast (SPEC §13); daarna past iemand ze
 * hier aan.
 *
 * Alinea's scheiden met een witregel. Alleen korte streepjes.
 */

/**
 * De gegevens van de Stichting die op de site horen te staan (Mollie eist
 * handelsnaam en KvK-nummer om te zien dat de site van de rekeninghouder
 * is). Als adres staat het clubhuis erbij; het postadres van de Stichting
 * bewust niet, dat is een privéadres.
 * Dezelfde gegevens staan statisch in `index.html`, voor wie de pagina
 * zonder JavaScript leest; houd die twee gelijk.
 */
export const STICHTING = {
  naam: 'Stichting tot behartiging van de belangen van de Hilfertheemgroep',
  kvk: '41192222',
  plaats: 'Hilversum',
  /** Het clubhuis, niet het postadres van de Stichting. */
  adres: 'Clubhuis De Spaander, Binnenweg 2, 1217 DE Hilversum',
  email: 'helden@hilfertheem.nl',
  website: 'https://www.hilfertheem.nl',
  heldenSite: 'https://helden.hilfertheem.nl',
  /** In CC bij elke mail aan de penningmeester (besluit 12 oktober 2026). */
  bestuurEmail: 'stichtingsbestuur@hilfertheem.nl',
} as const;

/**
 * Het blok "De échte helden" onder de intro: de vrijwilligers in getallen.
 * Met de hand bijhouden (een commit en een uitrol), net als de teksten.
 */
export const ECHTE_HELDEN = {
  /** Twee regels, onder elkaar. */
  wie: ['Leiding van de speltakken, bestuursleden, klusteam,', 'en alle andere vrijwilligers.'],
  vrijwilligers: 35,
  jarenVrijwilligerswerk: 521,
  jeugdleden: 91,
} as const;

export const TEKSTEN = {
  /**
   * Onder de titel op de muur: van wie dit is. Op verzoek van het
   * verenigingsbestuur (12 oktober 2026), zodat niemand het voor een actie van
   * de vereniging of de leiding aanziet.
   */
  afzender: 'Een initiatief van de Stichting die clubhuis De Spaander beheert',

  intro:
    'Scouting Hilfertheem draait al ruim een eeuw op vrijwilligers, op een prachtige plek in het Spanderswoud. De échte helden zijn natuurlijk de leiding en alle andere vrijwilligers, die zich elke week met hart en ziel inzetten voor onze jeugdleden. De “Helden van de Hilfertheem” op deze pagina zijn de helden op de achtergrond: mensen die de groep een warm hart toedragen en dat omzetten in een vaste bijdrage - zodat het clubhuis onderhouden blijft, het materiaal vernieuwd wordt en de kachel (letterlijk) kan blijven branden.',

  /** Als opsomming op de muur, één regel per punt. */
  wat_je_krijgt: [
    'Een plek op deze heldenmuur.',
    'Een vermelding in de nieuwsbrief.',
    'Een uitnodiging voor de jaarlijkse heldenborrel.',
    'Én de wetenschap dat er dankzij jou weer een tent, een nieuwe vloer of een lik verf bij is gekomen.',
  ],

  waar_het_heen_gaat:
    'Je bijdrage gaat naar de Stichting tot behartiging van de belangen van de Hilfertheemgroep. Die beheert en onderhoudt clubhuis De Spaander en het terrein in het Spanderswoud, en zorgt voor het grote materiaal, zodat de leiding zich met het scouten zelf kan bezighouden.',

  bedrijven:
    'Ook een bedrijf kan held zijn. Je logo staat op deze muur en in de nieuwsbrief, en je bent uiteraard ook welkom op de exclusieve heldenborrel. Gouden bedrijven krijgen hun logo bovendien achter de bar in het clubhuis. Voor je administratie krijg je een factuur voor "sponsoring Helden van de Hilfertheem", die je als sponsor- of reclamekosten kunt opvoeren.',

  voorwaarden: `Door held te worden machtig je de Stichting tot behartiging van de belangen van de Hilfertheemgroep om jaarlijks de heldenbijdrage van je rekening af te schrijven: voor het eerst bij aanmelding, daarna elk jaar in januari voor het nieuwe kalenderjaar. Je krijgt twee weken van tevoren bericht. Ben je het niet eens met een afschrijving, dan kun je die binnen acht weken via je bank laten terugboeken.

Een heldenjaar is een kalenderjaar. Wie zich vanaf september aanmeldt, betaalt voor het volgende jaar. Een eigen, hoger bedrag mag altijd; dat wordt dan jaarlijks afgeschreven tot je het wijzigt.

Opzeggen kan altijd, per direct, via je persoonlijke pagina of door een mail aan helden@hilfertheem.nl. Over het lopende jaar vindt geen restitutie plaats. Een afgekocht niveau (zilver of goud) geldt zolang je held bent.

De bijdrage is een gift aan de Stichting. Wat we met je gegevens doen staat in de privacyverklaring.`,

  privacy: `Helden van de Hilfertheem is een initiatief van de Stichting tot behartiging van de belangen van de Hilfertheemgroep (Hilversum), KvK-nummer 41192222. De Stichting is verantwoordelijk voor de verwerking van je gegevens; vragen en verzoeken kun je mailen naar helden@hilfertheem.nl.

We bewaren je naam, e-mailadres, eventueel telefoonnummer, de naam en regel die je op de muur wilt, en je betalingen. Je rekeningnummer bewaren wij niet: dat staat bij onze betaaldienstverlener Mollie, die het machtigingsmandaat beheert. Van dat mandaat kennen wij alleen de tenaamstelling en de laatste vier cijfers.

We gebruiken je gegevens om je bijdrage te innen, je op de muur en in de nieuwsbrief te vermelden (als je dat wilt) en je uit te nodigen voor de heldenborrel. We delen ze met niemand anders. Mail gaat via Resend (EU).

Twee jaar na opzegging anonimiseren we je gegevens; de betalingen blijven voor de boekhouding bewaard. Inzage, correctie of verwijdering: helden@hilfertheem.nl.`,
} as const;

export type Tekstsleutel = keyof typeof TEKSTEN;
